// frontend/src/services/playerService.js
// Player and roster operations
import { supabase } from './supabaseCore';

// Batch import players (CSV)
export const importPlayers = async (players, eventName, teamName) => {
    try {
        // Prepare records for event_uploads table
        const records = players.map(player => ({
            event_name: eventName,
            team: teamName || player.team,
            player_name: player.name,
            first_name: player.first_name || null,
            last_name: player.last_name || null,
            dob: player.dob || null,
            gender: player.gender || null,
            nationality: player.nationality || null,
            // Set when the importer confirmed a Sportradar link for this person.
            person_id: player.person_id || null,
            // Sportradar's own CDN URL, kept as the external reference the app already
            // stores for linked players. photo_url points at our copy.
            sportradar_photo_url: player.sportradar_photo_url || null,
            upload_status: false
        }));

        const { data, error } = await supabase
            .from('event_uploads')
            .insert(records)
            .select();

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error importing players:', error);
        return { data: null, error };
    }
};

// Bulk import teams and players with duplicate checking
export const bulkImportRoster = async (eventId, eventShortName, rows) => {
    try {
        const stats = { teamsCreated: 0, teamsSkipped: 0, playersCreated: 0, playersSkipped: 0 };

        // 1. Get existing teams for this event
        const { data: existingTeams } = await supabase
            .from('teams')
            .select('name, access_code')
            .eq('event_id', eventId);

        const existingTeamNames = new Set((existingTeams || []).map(t => t.name.toLowerCase()));

        // 2. Get existing players for this event
        const { data: existingPlayers } = await supabase
            .from('event_uploads')
            .select('team, player_name')
            .eq('event_name', eventShortName);

        const existingPlayerKeys = new Set(
            (existingPlayers || []).map(p => `${p.team.toLowerCase()}|${p.player_name.toLowerCase()}`)
        );

        // 3. Extract unique team names from CSV
        const uniqueTeams = [...new Set(rows.map(r => r.team_name))];

        // 4. Create teams that don't exist
        for (const teamName of uniqueTeams) {
            if (existingTeamNames.has(teamName.toLowerCase())) {
                stats.teamsSkipped++;
                continue;
            }

            // Generate access code: first 3 chars + 3 random digits
            const prefix = teamName.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'X');
            const randomNum = Math.floor(100 + Math.random() * 900);
            const accessCode = `${prefix}${randomNum}`;

            const { error } = await supabase
                .from('teams')
                .insert([{ event_id: eventId, name: teamName, access_code: accessCode }]);

            if (!error) {
                stats.teamsCreated++;
                existingTeamNames.add(teamName.toLowerCase());
            }
        }

        // 5. Create players that don't exist
        const playersToInsert = [];
        for (const row of rows) {
            const key = `${row.team_name.toLowerCase()}|${row.player_name.toLowerCase()}`;
            if (existingPlayerKeys.has(key)) {
                stats.playersSkipped++;
                continue;
            }

            playersToInsert.push({
                event_name: eventShortName,
                team: row.team_name,
                player_name: row.player_name,
                first_name: row.first_name || null,
                last_name: row.last_name || null,
                upload_status: false
            });
            existingPlayerKeys.add(key);
            stats.playersCreated++;
        }

        if (playersToInsert.length > 0) {
            const { error } = await supabase
                .from('event_uploads')
                .insert(playersToInsert);

            if (error) throw error;
        }

        return { stats, error: null };
    } catch (error) {
        console.error('Error bulk importing roster:', error);
        return { stats: null, error };
    }
};

/** Point a player at a photo that is already in Supabase storage. */
export const setPlayerPhoto = async (playerId, photoUrl) => {
    try {
        const { error } = await supabase
            .from('event_uploads')
            .update({ photo_url: photoUrl, upload_status: true, updated_at: new Date().toISOString() })
            .eq('id', playerId);

        if (error) throw error;
        return { error: null };
    } catch (error) {
        console.error('Error setting player photo:', error);
        return { error };
    }
};

// Search players across all events
export const globalSearch = async (query) => {
    try {
        const { data, error } = await supabase
            .rpc('search_players', { search_query: query });

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error searching players:', error);
        return { data: null, error };
    }
};

// Get recent player uploads for directory feed
export const getRecentPlayers = async (limit = 20, offset = 0) => {
    try {
        const { data, error } = await supabase
            .rpc('get_recent_uploads', { limit_val: limit, offset_val: offset });

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error fetching recent players:', error);
        return { data: null, error };
    }
};

// Delete Player (Database + Storage)
export const deletePlayer = async (player, currentEventCode) => {
    try {
        // 1. Delete photo from storage if exists
        if (player.photo_url) {
            // Regex to extract path from URL
            const pathMatch = player.photo_url.match(new RegExp(`${currentEventCode}/.*$`));
            if (pathMatch) {
                await supabase.storage.from('player-photos').remove([pathMatch[0]]);
            }
        }

        // 2. Delete no-bg photo if exists
        if (player.nobg_photo_url) {
            const pathMatch = player.nobg_photo_url.match(new RegExp(`${currentEventCode}/.*$`));
            if (pathMatch) {
                await supabase.storage.from('player-photos').remove([pathMatch[0]]);
            }
        }

        // 3. Delete from DB
        const { error } = await supabase
            .from('event_uploads')
            .delete()
            .eq('id', player.id);

        if (error) throw error;

        return { error: null };
    } catch (error) {
        console.error('Delete Player Error:', error);
        return { error };
    }
};
